import ReactApexChart from "react-apexcharts";
import { theme } from "../../../Theme/index";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getUrl } from "src/Utils/Common";
import { useMediaQuery } from "@mui/material";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Nav, NavDropdown } from "react-bootstrap";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";

const months = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const quarters = [
  { label: "Q1", value: 1 },
  { label: "Q2", value: 2 },
  { label: "Q3", value: 3 },
  { label: "Q4", value: 4 },
];

function BarChartWobContribution(props) {
  const {
    selectedHierarchy: propSelectedHierarchy,
    setSelectedHierarchy: propSetSelectedHierarchy,
    value: propValue,
    setValue: propSetValue,
    isManufacturer: propIsManufacturer,
    setIsManufacturer: propSetIsManufacturer,
    selected: propSelected,
    setSelected: propSetSelected,
    ...rest
  } = props;

  const dispatch = useDispatch(); // Import from react-redux

  const isMobile = useMediaQuery("(max-width: 600px)");
  const isSmallMobile = useMediaQuery("(max-width: 500px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1300px)");

  const min = 0;
  const max = 100;
  const currentChartType = useSelector((state) => state.chart.currentChartType);
  const selectedFilter = useSelector((state) => state.filter.selectedFilter); // ✅ Get Redux filter
  const projectId = useSelector((state) => state.userProject.projectId);
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [loading, setLoading] = useState(true); 
  const [maxY, setMaxY] = useState(0);

  const [localSelectedHierarchy, setLocalSelectedHierarchy] = useState(
    propSelectedHierarchy || "brand"
  );
  const selectedHierarchy =
    propSelectedHierarchy !== undefined
      ? propSelectedHierarchy
      : localSelectedHierarchy;
  const setSelectedHierarchy =
    propSetSelectedHierarchy || setLocalSelectedHierarchy;

  const [localValue, setLocalValue] = useState(0);
  const value = propValue !== undefined ? propValue : localValue;
  const setValue = propSetValue || setLocalValue;

  const [localIsManufacturer, setLocalIsManufacturer] = useState(false);
  const isManufacturer =
    propIsManufacturer !== undefined ? propIsManufacturer : localIsManufacturer;
  const setIsManufacturer = propSetIsManufacturer || setLocalIsManufacturer;

  const [localSelected, setLocalSelected] = useState({
    type: "years",
    value: null,
  });
  const selected = propSelected !== undefined ? propSelected : localSelected;
  const setSelected = propSetSelected || setLocalSelected;

  // State for data values and categories
  const [dataValues, setDataValues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().subtract(2, "year"));
  const [endDate, setEndDate] = useState(dayjs().subtract(1, "year"));
  const [timeFilter, setTimeFilter] = useState("years");

  const name = "Contribution to Growth-Decline";

  // ✅ Construct API filters dynamically based on selectedFilter
  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };

  useEffect(() => {
    function fetchHierarchyLevels() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        return;
      }

      fetch(`${getUrl()}/market_overview/hierarchy`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({ project_id: projectId }),
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          const hierarchyData =
            result?.["0"] ||
            result?.Product ||
            result?.product ||
            {};
          const hierarchyLevels = Object.keys(hierarchyData);

          const filteredHierarchyLevels = hierarchyLevels.filter(level => 
            !["size_bracket", "size", "price_bracket", "sku_db", "sku_id"].includes(level)
          );
          const finalHierarchyOptions = [
            ...filteredHierarchyLevels,
            "channel",
            "retailer"
          ];
          setHierarchyOptions(finalHierarchyOptions);
          setSelectedHierarchy((prev) => {
            if (!prev || !finalHierarchyOptions.includes(prev)) {
              return filteredHierarchyLevels.includes("brand")
                ? "brand"
                : filteredHierarchyLevels[0] || "";
            }
            return prev;
          });
        }) 
        .catch ((error) => {
        console.error("Error fetching hierarchy levels:", error);
      });
    }

    fetchHierarchyLevels();
  }, [projectId]);

  
  const fetchGrowthContribution = async () => {
    if (!projectId) {
      console.warn("Project ID is not set in Redux state.");
      return;
    }
    setLoading(true);
    let [hierarchy, hierarchy_value] = currentChartType?.split(",") || [
      "brand",
      "AFIA",
    ];

      hierarchy = selectedHierarchy !== null ? selectedHierarchy : hierarchy; // Override only if selected

    const filters = getApiFilters();

    try {
      const response = await fetch(`${getUrl()}/market_overview/growth_contribution`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({
          project_id: projectId,
          hierarchy: selectedHierarchy,
          filters: getApiFilters(),
          time_period: selected.type,
          area: null,
          target: selected.value,
          is_manufacturer: isManufacturer,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      if (
        result &&
        typeof result === "object" &&
        typeof result.debug === "string" &&
        result.debug.toLowerCase().includes("sql returned 0 rows")
      ) {
        setDataValues([]);
        setCategories([]);
        setMaxY(0);
        setLoading(false);
        return;
      }

      if (
        result &&
        typeof result === "object" &&
        Array.isArray(result.index) &&
        result.index.length === 0
      ) {
        const contribEarly = result["Contribution to Growth"];
        const categoriesEarly = result[hierarchy];
        const hasSeries =
          Array.isArray(contribEarly) &&
          contribEarly.length > 0 &&
          Array.isArray(categoriesEarly) &&
          categoriesEarly.length > 0;
        if (!hasSeries) {
          setDataValues([]);
          setCategories([]);
          setMaxY(0);
          setLoading(false);
          return;
        }
      }

      const contribution = result["Contribution to Growth"];
      const categoryLabels = result[hierarchy];
      if (
        !Array.isArray(contribution) ||
        !Array.isArray(categoryLabels) ||
        contribution.length === 0 ||
        categoryLabels.length === 0
      ) {
        setDataValues([]);
        setCategories([]);
        setMaxY(0);
        setLoading(false);
        return;
      }

      setMaxY(Math.max(...contribution));
      setCategories(categoryLabels);
      setDataValues([
        {
          type: "bar",
          name: "Contribution to Growth",
          data: contribution,
        },
      ]);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching growth contribution data:", error);
      setDataValues([]);
      setCategories([]);
      setMaxY(0);
      setLoading(false);
    }
  };

  const resetChartData = () => {
    setDataValues([]);
    setCategories([]);
    setMaxY(0);
  };

  const resetChartFilters = () => {
    setLocalSelectedHierarchy("brand");
    setLocalValue(0);
    setLocalIsManufacturer(false);
    setLocalSelected({ type: "years", value: null });
    setStartDate(dayjs().subtract(2, "year"));
    setEndDate(dayjs().subtract(1, "year"));
    setTimeFilter("years");
    setSelected([]);
  };

  const resetChartError = () => {
  };

  const resetChartLoading = () => {
    setLoading(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(fetchGrowthContribution, [], {
    resetMode: true,
    resetFunctions: {
      resetData: resetChartData,
      resetFilters: resetChartFilters,
      resetError: resetChartError,
      resetLoading: resetChartLoading,
    }
  });

  useEffect(() => {
    if (projectId) {
      fetchGrowthContribution();
    }
  }, [
    projectId,
    selectedFilter,
    selectedHierarchy,
    startDate,
    endDate,
    timeFilter,
    isManufacturer,
    selected,
  ]);

  // // Selector to retrieve Redux state for triggering updates
  // const currentDateComparison = useSelector(
  //   (state) => state.dateCompare.currentDateComparison
  // );

  // // Simulate data updates when `currentDateComparison` changes
  // useEffect(() => {
  //   const updatedData = Array.from(
  //     { length: 7 },
  //     () => (Math.random() * 200 - 100).toFixed(1) // Generate random values between -100 and 100
  //   );

  //   setDataValues([
  //     {
  //       type: "bar",
  //       name: "Contribution to Growth",
  //       data: updatedData,
  //     },
  //   ]);

  //   console.log(`Updated data for currentDateComparison: ${updatedData}`);
  // }, [currentDateComparison]);

  const handleChangeTab = (event, newValue) => {
    if (newValue === 0) {
      setIsManufacturer(false);
    } else if (newValue === 1) {
      setIsManufacturer(true);
    }
    setValue(newValue);
  };

  const handleSelect = (type, value = null) => {
    setSelected({ type, value });
  };

  const getTitle = () => {
    if (!selected.type) return "MAT24";
    if (selected.type === "years") return "Current Year vs Last Year";
    if (selected.type === "P12M") return "MAT24";
    if (selected.type === "months") {
      const label = months.find((m) => m.value === selected.value)?.label;
      return `Current ${label} vs ${label} Last Year`;
    }
    if (selected.type === "months_SameYear") {
      const label = months.find((m) => m.value === selected.value)?.label;
      return `${label} vs It's Previous Month`;
    }
    if (selected.type === "quarters") {
      const label = quarters.find((q) => q.value === selected.value)?.label;
      return `Current ${label} vs ${label} Last Year`;
    }
    return "Select Time Filter";
  };

  const getYAxisFormatter = (val) => {
    return `${val.toFixed(0)}%`;
  };

  // Chart options Contribution to Growth"
  const options = React.useMemo(
    () => ({
      chart: {
        toolbar: { show: true },
        animations: { enabled: true },
        zoom: { enabled: true },
        height: "100%",
        parentHeightOffset: 0,
      },
      stroke: { show: false },
      colors: value === 0 ? ["#08306B"]:["#21A0FF"], // 👈 Change based on value
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val}%`,
        offsetY: isSmallMobile ? -3 : isMobile ? -5 : -10,
        style: {
          fontSize: isSmallMobile ? "7px" : isMobile ? "8px" : "11px",
        },
        background: {
          enabled: false, // ⬅️ removes background box
        },
      },
      xaxis: {
        categories: categories,
        labels: {
          show: true,
          rotate: isSmallMobile ? -60 : -45,
          rotateAlways: true,
          style: {
            fontSize: isSmallMobile ? "7px" : isMobile ? "8px" : "11px",
            fontWeight: 500,
          },
          trim: false,
        },
        tickPlacement: "on",
        tooltip: { enabled: true },
        axisBorder: { show: false },
      },
      yaxis: {
        min: min,
        max: maxY,
        title: { text: "Value Market Share" },
        labels: { formatter: getYAxisFormatter },
      },
      plotOptions: {
        bar: {
          borderRadius: 0,
          columnWidth: isSmallMobile ? "70%" : isMobile ? "60%" : "25%",
          dataLabels: { position: "top" },
        },
      },
      fill: { opacity: 1 },
      responsive: [
        {
          breakpoint: 500,
          options: {
            chart: { width: "100%" },
            legend: { position: "bottom" },
            xaxis: {
              labels: {
                rotate: -60,
                rotateAlways: true,
                style: { fontSize: "7px" },
              },
            },
            dataLabels: {
              style: { fontSize: "7px" },
              offsetY: -3,
            },
            plotOptions: {
              bar: { columnWidth: "70%" },
            },
          },
        },
        {
          breakpoint: 600,
          options: {
            chart: { width: "120%" },
            legend: { position: "bottom" },
            xaxis: { labels: { rotate: -45, rotateAlways: true } },
          },
        },
      ],
    }),
    [categories, value]
  );

  return (
    <Box 
      sx={(theme) => ({
        width: "100%",
        [theme.breakpoints.between("sm", 1300)]: {
          width: "70%",
          margin: "0 auto",
        },
      })}
    >
      {/* Header row: title left, filter right */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 4,
          height: isSmallMobile ? 40 : 48,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: {
              xs: isSmallMobile ? "0.85rem" : "0.9rem",        
              sm: "1rem",          
              md: "1.1rem",             
            },
            px: {
              xs: isSmallMobile ? 0.25 : 0.5,             
              sm: 0.75,            
              md: 1,               
            },
            color: theme.palette.primary.main,
            textAlign: "left",
            m: 0,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            height: isSmallMobile ? 40 : 48,
          }}
        >
          {name}
        </Typography>
        <CustomSelectBox
          value={hierarchyOptions.includes(selectedHierarchy) ? selectedHierarchy : ""}
          onChange={(event) => {
            const newHierarchy = event.target.value;
            setSelectedHierarchy(newHierarchy);
            // Extract hierarchy_value from currentChartType
            let [, hierarchy_value] = currentChartType?.split(",") || [
              "brand",
              "AFIA",
            ];
            // Override currentChartType with new hierarchy but keep the same hierarchy_value
            dispatch({
              type: "UPDATE_CURRENT_CHART_TYPE",
              payload: `${newHierarchy},${hierarchy_value}`,
            });
          }}
          options={hierarchyOptions}
        />
      </div>

      <Box 
        sx={{ 
          width: "100%",
          display: "flex",
          justifyContent: "center",
          mb: isSmallMobile ? 0.5 : { xs: 1, sm: 2 }
        }}
      >
        <Tabs
          value={value}
          onChange={handleChangeTab}
          centered
          textColor="primary"
          indicatorColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            width: isSmallMobile ? "80%" : isMobile ? "60%" : "auto",
            minHeight: isSmallMobile ? 36 : { xs: 40, sm: 48 },
            "& .MuiTab-root": {
              minWidth: isSmallMobile ? "auto" : { xs: "auto", sm: 90 },
              fontSize: isSmallMobile ? "0.8rem" : { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
              fontWeight: 500,
              px: isSmallMobile ? 0.5 : { xs: 1, sm: 2, md: 3 },
              py: isSmallMobile ? 0.25 : { xs: 0.5, sm: 1 },
              textTransform: "none",
              letterSpacing: "normal",
            },
            "& .MuiTabs-indicator": {
              height: isSmallMobile ? 1.5 : { xs: 2, sm: 3 },
            },
            "& .MuiTabs-flexContainer": {
              gap: isSmallMobile ? 0 : { xs: 0, sm: 1 },
            }
          }}
        >
          <Tab 
            label="Market" 
            sx={{
              minHeight: isSmallMobile ? 32 : { xs: 36, sm: 40 },
            }}
          />
          <Tab 
            label="Manufacturer" 
            sx={{
              minHeight: isSmallMobile ? 32 : { xs: 36, sm: 40 },
            }}
          />
        </Tabs>
      </Box>

      <Box justifyContent="left">
        <Nav>
          <NavDropdown title={getTitle()}>
            <NavDropdown.Item onClick={() => handleSelect("years")}>
              Current Year vs Last Year
            </NavDropdown.Item>

            <NavDropdown.Item onClick={() => handleSelect("P12M")}>
              MAT24
            </NavDropdown.Item>

            <NavDropdown title="Selected Month vs Month Year Ago" drop="end">
              {months.map((month) => (
                <NavDropdown.Item
                  key={month.value}
                  onClick={() => handleSelect("months", month.value)}
                >
                  {month.label}
                </NavDropdown.Item>
              ))}
            </NavDropdown>

            <NavDropdown title="Selected Month vs Pervious one" drop="end">
              {months.map((month) => (
                <NavDropdown.Item
                  key={month.value}
                  onClick={() => handleSelect("months_SameYear", month.value)}
                >
                  {month.label}
                </NavDropdown.Item>
              ))}
            </NavDropdown>

            <NavDropdown
              title="Selected Quarter vs Same Quarter Last Year"
              drop="end"
            >
              {quarters.map((quarter) => (
                <NavDropdown.Item
                  key={quarter.value}
                  onClick={() => handleSelect("quarters", quarter.value)}
                >
                  {quarter.label}
                </NavDropdown.Item>
              ))}
            </NavDropdown>
          </NavDropdown>
        </Nav>
      </Box>
      {loading || isRefreshing ? (
        <Box sx={(theme) => ({
          width: isSmallMobile ? "100%" : isMobile ? "120%" : 500,
          height: isSmallMobile ? 200 : 300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          [theme.breakpoints.between("sm", 1300)]: {
            width: "70%",
          },
        })}>
          <FacebookCircularProgress />
        </Box>
      ) : dataValues.length > 0 ? (
        <Box sx={(theme) => ({
          width: "100%",
          display: "flex",
          justifyContent: "center",
          [theme.breakpoints.between("sm", 1300)]: {
            width: "55%",
            margin: "0 auto",
          },
        })}>
          <ReactApexChart
            options={{ ...options, xaxis: { ...options.xaxis, categories } }}
            series={dataValues}
            width={isSmallMobile ? "100%" : isMobile ? "120%" : isTablet ? 400 : 500}
            height={isSmallMobile ? 200 : isMobile ? 250 : 300}
          />
        </Box>
      ) : (
        <Box sx={(theme) => ({
          width: isSmallMobile ? "100%" : isMobile ? "120%" : 500,
          height: isSmallMobile ? 200 : 300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          [theme.breakpoints.between("sm", 1300)]: {
            width: "70%",
          },
        })}>
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message="No data available - Click to reset chart"
          />
        </Box>
      )}
    </Box>
  );
}

export default BarChartWobContribution;
